// src/components/RegularUserDashboard.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
  Divider,
  useMediaQuery,
} from "@mui/material";
import {
  FaShoppingCart,
  FaTrash,
  FaSearch,
  FaSignOutAlt,
  FaMinus,
  FaPlus,
  FaTimes,
  FaFilter,
  FaChevronDown,
  FaChevronRight,
} from "react-icons/fa";

import logoKemenkes from "../assets/img/logo/logokk.png";
import api, { getApiBase } from "../api";
import ProductCard from "./ProductCard";

const DEFAULT_IMG = "/no-image.png";
const GRID_MIN_CARD_PX = 260;
const ROWS_PER_PAGE = 3;
const MAX_FETCH = 2000;

// ===== sanitize cart (tanpa satuan)
const sanitizeCart = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((it) => {
      const id = it?.id ?? it?.product_id ?? null;
      if (!id) return null;

      const qtyN = Number(it?.qty ?? 0);
      const qty = Number.isFinite(qtyN) ? Math.max(0, Math.trunc(qtyN)) : 0;

      return {
        id,
        name: it?.name ?? "Produk",
        category: it?.category || "Tidak Diketahui",
        subcategory: it?.subcategory || "",
        category_id: it?.category_id ?? null,
        main_category_id: it?.main_category_id ?? null,
        stock: it?.stock ?? null,
        maxStock: it?.maxStock ?? it?.stock ?? null,
        photo: it?.photo ?? null,
        image: it?.image ?? "",
        qty,
        sisa_stok: (it?.sisa_stok ?? "") === null ? "" : it?.sisa_stok ?? "",
        keperluan: it?.keperluan ?? "",
      };
    })
    .filter((x) => x && (Number(x.qty) || 0) > 0);
};

// ===== normalize kategori tree dari API
const normalizeToTree = (data) => {
  if (Array.isArray(data) && data.length > 0 && Object.prototype.hasOwnProperty.call(data[0], "children")) {
    return (data || []).map((p) => ({
      id: Number(p.id),
      name: String(p.name ?? "").trim(),
      parent_id: p.parent_id ?? null,
      children: Array.isArray(p.children)
        ? p.children.map((c) => ({
            id: Number(c.id),
            name: String(c.name ?? "").trim(),
            parent_id: c.parent_id ?? Number(p.id),
            children: [],
          }))
        : [],
    }));
  }

  const list = Array.isArray(data) ? data : [];
  const map = new Map();
  list.forEach((c) => {
    const id = Number(c.id ?? c.category_id ?? c.categoryId);
    if (!id) return;
    map.set(id, {
      id,
      name: String(c.name ?? c.nama ?? "").trim(),
      parent_id: c.parent_id ?? c.parentId ?? null,
      children: [],
    });
  });

  const roots = [];
  map.forEach((node) => {
    const pidRaw = node.parent_id;
    const pid = pidRaw === null || pidRaw === undefined || pidRaw === "" ? null : Number(pidRaw);
    node.parent_id = Number.isFinite(pid) ? pid : null;

    if (node.parent_id && map.has(node.parent_id)) {
      map.get(node.parent_id).children.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
};

const flattenTree = (tree) => {
  const out = [];
  (tree || []).forEach((p) => {
    out.push({ id: Number(p.id), name: String(p.name || "").trim(), parent_id: null, parent_name: null });
    (p.children || []).forEach((c) => {
      out.push({
        id: Number(c.id),
        name: String(c.name || "").trim(),
        parent_id: Number(p.id),
        parent_name: String(p.name || "").trim(),
      });
    });
  });
  return out.filter((x) => Number.isFinite(x.id) && x.name);
};

const normalizeText = (s = "") =>
  String(s)
    .toLowerCase()
    .replace(/&/g, " dan ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export default function RegularUserDashboard({ onLogout }) {
  const [userId] = useState(() => localStorage.getItem("user_id"));
  const isMobile = useMediaQuery("(max-width: 900px)");

  // ===== kategori
  const [categoriesTree, setCategoriesTree] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [selectedMainId, setSelectedMainId] = useState(0);
  const [selectedSubId, setSelectedSubId] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);

  // ===== produk
  const [products, setProducts] = useState([]);
  const [serverMeta, setServerMeta] = useState({
    current_page: 1,
    per_page: null,
    last_page: null,
    total: null,
  });

  // ===== keranjang
  const [cart, setCart] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("cart_v1") || "[]");
      return sanitizeCart(raw);
    } catch {
      return [];
    }
  });
  const [cartLoading, setCartLoading] = useState(false);
  const [cartSubmitting, setCartSubmitting] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);

  // dialog pengajuan
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitErr, setSubmitErr] = useState("");

  // search
  const [search, setSearch] = useState("");

  // logout
  const [loggingOut, setLoggingOut] = useState(false);

  // ===== pagination client
  const gridMeasureRef = useRef(null);
  const [gridCols, setGridCols] = useState(4);
  const itemsPerPage = Math.max(1, gridCols * ROWS_PER_PAGE);
  const [page, setPage] = useState(1);

  const makePhotoBase = () => {
    const envOverride = process.env.REACT_APP_PHOTO_BASE_URL;
    if (envOverride) return envOverride.replace(/\/+$/g, "");
    const origin = (getApiBase() || "").replace(/\/api\/?$/i, "");
    return `${origin}/storage/products`;
  };
  const PHOTO_BASE_URL = makePhotoBase();

  // ukuran kolom grid responsif
  useEffect(() => {
    const el = gridMeasureRef.current;
    if (!el) return;
    const computeCols = () => {
      const width = el.clientWidth || 0;
      const style = getComputedStyle(document.documentElement);
      const gapVar = parseFloat(style.getPropertyValue("--tp-gap") || "16");
      const gap = Number.isFinite(gapVar) ? gapVar : 16;
      const cols = Math.max(1, Math.floor((width + gap) / (GRID_MIN_CARD_PX + gap)));
      setGridCols(cols);
    };
    computeCols();
    const ro = new ResizeObserver(computeCols);
    ro.observe(el);
    window.addEventListener("resize", computeCols);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", computeCols);
    };
  }, []);

  // simpan cart
  useEffect(() => {
    localStorage.setItem("cart_v1", JSON.stringify(sanitizeCart(cart)));
  }, [cart]);

  // ===== map kategori by id
  const categoriesFlat = useMemo(() => flattenTree(categoriesTree), [categoriesTree]);
  const categoryById = useMemo(() => {
    const m = new Map();
    categoriesFlat.forEach((c) => m.set(Number(c.id), c));
    return m;
  }, [categoriesFlat]);

  const getCategoryParts = useCallback(
    (category_id, fallbackSubName = "") => {
      const idNum = Number(category_id);
      const cat = categoryById.get(idNum);

      if (cat) {
        if (cat.parent_name) {
          return {
            mainId: Number(cat.parent_id) || null,
            subId: Number(cat.id) || null,
            main: cat.parent_name,
            sub: cat.name,
          };
        }
        return {
          mainId: Number(cat.id) || null,
          subId: null,
          main: cat.name,
          sub: "-",
        };
      }

      return {
        mainId: null,
        subId: idNum || null,
        main: "LAINNYA",
        sub: fallbackSubName || "Tidak Diketahui",
      };
    },
    [categoryById]
  );

  // ===== fetch kategori (tree)
  const fetchCategories = useCallback(async () => {
    try {
      let res;
      try {
        res = await api.get("/categories", { params: { mode: "tree" } });
      } catch {
        res = await api.get("/categories");
      }

      const tree = normalizeToTree(res.data);

      tree.sort((a, b) => String(a.name).localeCompare(String(b.name), "id", { sensitivity: "base" }));
      tree.forEach((p) =>
        (p.children || []).sort((a, b) => String(a.name).localeCompare(String(b.name), "id", { sensitivity: "base" }))
      );

      setCategoriesTree(tree);

      if (tree[0]?.id) {
        setExpanded((prev) => {
          const next = new Set(prev);
          next.add(Number(tree[0].id));
          return next;
        });
      }
    } catch {
      setCategoriesTree([]);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // ===== fetch produk
  const fetchProducts = useCallback(
    async (pageParam, perPageParam) => {
      try {
        const isFilterActive = selectedMainId !== 0 || selectedSubId !== 0 || search.trim() !== "";

        const reqPage = isFilterActive ? 1 : pageParam;
        const reqPerPage = isFilterActive ? MAX_FETCH : perPageParam;

        const params = new URLSearchParams();
        params.set("page", String(reqPage));
        params.set("per_page", String(reqPerPage));

        if (search.trim() !== "") params.set("q", search.trim());

        if (selectedMainId !== 0) {
          const mainObj = categoriesTree.find((c) => Number(c.id) === Number(selectedMainId));
          if (mainObj?.name) params.set("category", mainObj.name);
          params.set("category_id", String(selectedMainId));
        }

        if (selectedSubId !== 0) {
          const mainObj = categoriesTree.find((c) => Number(c.id) === Number(selectedMainId));
          const subObj = mainObj?.children?.find((s) => Number(s.id) === Number(selectedSubId));
          if (subObj?.name) params.set("subcategory", subObj.name);
          params.set("subcategory_id", String(selectedSubId));
          params.set("category_id", String(selectedSubId));
        }

        const res = await api.get(`/products?${params.toString()}`);

        let raw = res.data;
        let list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];

        const mapped = list.map((p) => {
          const categoryId = p.category_id ?? p.categoryId ?? null;
          const fallbackSub = p.category?.name || p.category_name || p.categoryName || "";
          const parts = getCategoryParts(categoryId, fallbackSub);

          return {
            id: p.id,
            name: p.name,
            category_id: categoryId,
            main_category_id: parts.mainId,
            main_category: parts.main,
            subcategory_id: parts.subId,
            subcategory: parts.sub && parts.sub !== "-" ? parts.sub : "",
            stock: Number(p.stock ?? p.jumlah_stok ?? p.total_stock ?? 0),
            photo: p.photo,
            image: p.photo ? `${PHOTO_BASE_URL}/${p.photo}` : "",
          };
        });

        setProducts(mapped);

        const meta = raw?.meta || raw?.pagination || null;
        if (!isFilterActive && meta) {
          setServerMeta({
            current_page: Number(meta.current_page ?? pageParam ?? 1),
            per_page: Number(meta.per_page ?? perPageParam ?? mapped.length),
            last_page: Number(meta.last_page ?? null),
            total: Number(meta.total ?? null),
          });
        } else {
          setServerMeta({ current_page: 1, per_page: reqPerPage, last_page: null, total: null });
        }
      } catch {
        setProducts([]);
        setServerMeta({ current_page: 1, per_page: null, last_page: null, total: null });
      }
    },
    [PHOTO_BASE_URL, getCategoryParts, selectedMainId, selectedSubId, search, categoriesTree]
  );

  useEffect(() => {
    fetchProducts(page, itemsPerPage);
  }, [fetchProducts, page, itemsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [selectedMainId, selectedSubId, search, itemsPerPage]);

  // ===== filtering client
  const filteredProducts = useMemo(() => {
    let list = products;

    if (selectedMainId !== 0) list = list.filter((p) => Number(p.main_category_id) === Number(selectedMainId));
    if (selectedSubId !== 0) list = list.filter((p) => Number(p.subcategory_id) === Number(selectedSubId));

    const q = normalizeText(search);
    if (q) {
      list = list.filter((p) => {
        return (
          normalizeText(p.name).includes(q) ||
          normalizeText(p.main_category).includes(q) ||
          normalizeText(p.subcategory).includes(q)
        );
      });
    }
    return list;
  }, [products, selectedMainId, selectedSubId, search]);

  const isFilterActive = selectedMainId !== 0 || selectedSubId !== 0 || search.trim() !== "";
  const isServerPaginated = !isFilterActive && (serverMeta.last_page != null || serverMeta.total != null);

  const totalPages = isServerPaginated
    ? serverMeta.last_page || Math.max(1, Math.ceil((serverMeta.total || products.length) / itemsPerPage))
    : Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage));

  const pageItems = isServerPaginated
    ? products
    : filteredProducts.slice((page - 1) * itemsPerPage, (page - 1) * itemsPerPage + itemsPerPage);

  const totalCartItems = useMemo(() => cart.reduce((s, i) => s + (Number(i.qty) || 0), 0), [cart]);

  const reservedById = useMemo(() => {
    const m = new Map();
    (cart || []).forEach((it) => m.set(it.id, (m.get(it.id) || 0) + (Number(it.qty) || 0)));
    return m;
  }, [cart]);

  // ===== keranjang sync
  useEffect(() => {
    if (!cartOpen || !userId) return;
    if (cart && cart.length > 0) return;

    (async () => {
      try {
        setCartLoading(true);
        const res = await api.get(`/keranjang/${userId}`);
        const itemsFromServer = res.data?.data?.items || [];

        setCart(
          sanitizeCart(
            itemsFromServer.map((it) => ({
              id: it.id,
              name: it.name,
              category: it.main_category || it.category || "Tidak Diketahui",
              subcategory: it.subcategory || "",
              category_id: it.category_id ?? null,
              main_category_id: it.main_category_id ?? null,
              stock: it.stock,
              maxStock: it.stock,
              photo: it.photo,
              image: it.photo ? `${PHOTO_BASE_URL}/${it.photo}` : "",
              qty: Number(it.qty) || 0,
              sisa_stok: (it.sisa_stok ?? "") === null ? "" : it.sisa_stok ?? "",
              keperluan: it.keperluan ?? "",
            }))
          )
        );
      } finally {
        setCartLoading(false);
      }
    })();
  }, [cartOpen, userId, PHOTO_BASE_URL, cart]);

  // ===== handlers cart
  const handleAddToCart = (p) => {
    const reserved = reservedById.get(p.id) || 0;
    const realStock = Number(p.stock) || 0;
    if (reserved >= realStock) return alert("Stok produk ini sudah habis untuk keranjang Anda.");

    setCart((prev) => {
      const safePrev = sanitizeCart(prev);

      const exist = safePrev.find((i) => i.id === p.id);
      if (exist) {
        if (Number(exist.qty) < realStock) {
          return safePrev.map((i) => (i.id === p.id ? { ...i, qty: Number(i.qty) + 1 } : i));
        }
        alert("Jumlah di keranjang sudah mencapai stok maksimum!");
        return safePrev;
      }

      return sanitizeCart([
        ...safePrev,
        {
          id: p.id,
          name: p.name,
          category: p.main_category || "Tidak Diketahui",
          subcategory: p.subcategory || "",
          category_id: p.category_id,
          main_category_id: p.main_category_id,
          stock: realStock,
          maxStock: realStock,
          photo: p.photo,
          image: p.image || "",
          qty: 1,
          sisa_stok: "",
          keperluan: "",
        },
      ]);
    });

    setCartOpen(true);
  };

  const handleQtyChange = (id, newQty, stock) => {
    const q = Math.trunc(Number(newQty));
    const max = Number.isFinite(Number(stock)) ? Number(stock) : Infinity;
    if (!Number.isInteger(q) || q < 1 || q > max) {
      return alert(`Jumlah harus antara 1 dan ${Number.isFinite(max) ? max : "tanpa batas"}.`);
    }
    setCart((prev) => sanitizeCart(prev.map((i) => (i.id === id ? { ...i, qty: q } : i))));
  };

  const handleRemoveFromCart = (id) => setCart((prev) => sanitizeCart(prev.filter((i) => i.id !== id)));

  const setCartField = (id, field, value) => {
    setCart((prev) => sanitizeCart(prev.map((i) => (i.id === id ? { ...i, [field]: value } : i))));
  };

  const handleLogout = useCallback(async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      if (typeof onLogout === "function") {
        try {
          await onLogout();
        } catch {}
      }
      try {
        await api.post("/logout", {});
      } catch {}
    } finally {
      try {
        localStorage.clear();
        sessionStorage.clear();
      } finally {
        window.location.assign("/login");
      }
    }
  }, [onLogout, loggingOut]);

  const handleClearCart = useCallback(() => {
    setCart([]);
    try {
      localStorage.setItem("cart_v1", "[]");
    } catch {}
  }, []);

  const openSubmitDialog = () => {
    if (!cart.length) return;
    setSubmitErr("");
    setSubmitOpen(true);
  };

  const validateBeforeSubmit = () => {
    if (!userId) return "Sesi login habis, silakan login ulang.";
    if (!cart.length) return "Keranjang kosong.";

    for (const it of cart) {
      const sisa = String(it.sisa_stok ?? "").trim();
      if (sisa === "" || !Number.isFinite(Number(sisa)) || Number(sisa) < 0) {
        return `Sisa Stok wajib diisi (angka >= 0) untuk: ${it.name}`;
      }
      const kep = String(it.keperluan ?? "").trim();
      if (!kep) return `Keperluan wajib diisi untuk: ${it.name}`;
    }
    return "";
  };

  const handleSubmitCart = useCallback(async () => {
    if (cartSubmitting) return;

    const msg = validateBeforeSubmit();
    if (msg) {
      setSubmitErr(msg);
      return;
    }

    try {
      setCartSubmitting(true);
      setSubmitErr("");

      await api.post("/keranjang", {
        user_id: userId,
        items: cart.map((i) => ({
          product_id: i.id,
          qty: i.qty,
          sisa_stok: parseInt(i.sisa_stok, 10),
          keperluan: i.keperluan,
        })),
      });

      setCart([]);
      setSubmitOpen(false);
      setCartOpen(false);

      await fetchProducts(page, itemsPerPage);
      alert("Pengajuan berhasil dikirim!");
    } catch (e) {
      const text = e?.response?.data?.message || "Gagal mengirim pengajuan! Pastikan API backend berjalan.";
      setSubmitErr(text);
    } finally {
      setCartSubmitting(false);
    }
  }, [cart, cartSubmitting, userId, fetchProducts, page, itemsPerPage]);

  // ===== Pagination UI
  const Pagination = ({ sticky = false }) => {
    const totalCount = isServerPaginated ? serverMeta.total ?? products.length : filteredProducts.length;
    if (totalCount === 0) return null;

    const windowSize = isMobile ? 5 : 7;
    let start = Math.max(1, page - Math.floor(windowSize / 2));
    const totalPagesSafe = Math.max(1, totalPages);
    let end = Math.min(totalPagesSafe, start + windowSize - 1);
    if (end - start + 1 < windowSize) start = Math.max(1, end - windowSize + 1);

    const nums = [];
    for (let i = start; i <= end; i++) nums.push(i);

    const showing = pageItems.length;

    return (
      <div className={`tp-pager ${sticky ? "sticky" : ""}`}>
        <span className="tp-pageinfo">
          Halaman <b>{page}</b> dari <b>{totalPagesSafe}</b> — Menampilkan <b>{showing}</b> / {totalCount}
        </span>

        <button className="tp-pagebtn" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
          ⟨ Sebelumnya
        </button>

        {start > 1 && (
          <>
            <button className="tp-pagenum" onClick={() => setPage(1)}>
              1
            </button>
            {start > 2 && <span className="tp-ellipsis">…</span>}
          </>
        )}

        {nums.map((n) => (
          <button key={n} className={n === page ? "tp-pagenum active" : "tp-pagenum"} onClick={() => setPage(n)}>
            {n}
          </button>
        ))}

        {end < totalPagesSafe && (
          <>
            {end < totalPagesSafe - 1 && <span className="tp-ellipsis">…</span>}
            <button className="tp-pagenum" onClick={() => setPage(totalPagesSafe)}>
              {totalPagesSafe}
            </button>
          </>
        )}

        <button
          className="tp-pagebtn"
          disabled={page === totalPagesSafe}
          onClick={() => setPage((p) => Math.min(totalPagesSafe, p + 1))}
        >
          Berikutnya ⟩
        </button>
      </div>
    );
  };

  // ===== render filter list
  const renderFilterContent = () => (
    <div className="tp-filter" aria-label="Filter produk">
      <div className="tp-filter-group">
        <h3>Kategori</h3>

        <div
          className={selectedMainId === 0 ? "tp-radio active" : "tp-radio"}
          onClick={() => {
            setSelectedMainId(0);
            setSelectedSubId(0);
            setPage(1);
            if (isMobile) setFilterOpen(false);
          }}
          role="button"
        >
          <b>SEMUA</b>
        </div>

        {(categoriesTree || []).map((main) => {
          const mainId = Number(main.id);
          const isExpanded = expanded.has(mainId);
          const isActiveMain = selectedMainId === mainId && selectedSubId === 0;

          return (
            <div key={mainId} className="tp-cat-block">
              <div className="tp-cat-head">
                <div
                  className={isActiveMain ? "tp-radio active tp-radio-main" : "tp-radio tp-radio-main"}
                  onClick={() => {
                    setSelectedMainId(mainId);
                    setSelectedSubId(0);
                    setPage(1);
                    setExpanded((prev) => {
                      const next = new Set(prev);
                      next.add(mainId);
                      return next;
                    });
                    if (isMobile) setFilterOpen(false);
                  }}
                  role="button"
                >
                  {main.name}
                </div>

                <button
                  type="button"
                  className="tp-expand"
                  onClick={() => {
                    setExpanded((prev) => {
                      const next = new Set(prev);
                      if (next.has(mainId)) next.delete(mainId);
                      else next.add(mainId);
                      return next;
                    });
                  }}
                  aria-label="Toggle sub kategori"
                >
                  {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                </button>
              </div>

              {isExpanded && (main.children || []).length > 0 && (
                <div className="tp-sublist">
                  <div
                    className={selectedMainId === mainId && selectedSubId === 0 ? "tp-subitem active" : "tp-subitem"}
                    onClick={() => {
                      setSelectedMainId(mainId);
                      setSelectedSubId(0);
                      setPage(1);
                      if (isMobile) setFilterOpen(false);
                    }}
                    role="button"
                  >
                    Semua
                  </div>

                  {(main.children || []).map((sub) => {
                    const subId = Number(sub.id);
                    const isActiveSub = selectedMainId === mainId && selectedSubId === subId;

                    return (
                      <div
                        key={subId}
                        className={isActiveSub ? "tp-subitem active" : "tp-subitem"}
                        onClick={() => {
                          setSelectedMainId(mainId);
                          setSelectedSubId(subId);
                          setPage(1);
                          if (isMobile) setFilterOpen(false);
                        }}
                        role="button"
                      >
                        {sub.name}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {(selectedMainId !== 0 || selectedSubId !== 0 || search.trim() !== "") && (
        <button
          type="button"
          className="tp-clear"
          onClick={() => {
            setSelectedMainId(0);
            setSelectedSubId(0);
            setSearch("");
            setPage(1);
            if (isMobile) setFilterOpen(false);
          }}
        >
          Hapus Semua Filter
        </button>
      )}
    </div>
  );

  return (
    <div className="tp-shell">
      <style>{`
        :root{
          --tp-pad: clamp(12px, 2vw, 20px);
          --tp-gap: clamp(12px, 1.4vw, 18px);
        }

        .tp-shell{
          background:#f5f7f6;
          color:#0f172a;
          font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial;
        }

        .tp-topbar{
          position: sticky;
          top: 0;
          z-index: 1000;
          background: rgba(255,255,255,.96);
          border-bottom: 1px solid #eef1f4;
          backdrop-filter:saturate(160%) blur(6px);
          width: 100%;
        }

        .tp-topbar-inner{
          width:100%;
          padding:12px var(--tp-pad);
          display:flex;
          align-items:center;
          gap:12px;
          flex-wrap:wrap;
        }

        .tp-brand-wrap{
          display:flex;
          align-items:center;
          gap:10px;
          min-width:0;
          flex: 1 1 auto;
        }
        .tp-logo{ height:44px; object-fit:contain; }
        .tp-title{
          margin:0;
          font-weight:800;
          font-size:clamp(14px,1.8vw,18px);
          color:#0f172a;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
          max-width:52vw;
        }

        .tp-search{
          display:flex;
          align-items:center;
          gap:10px;
          border:1px solid #e3e9f0;
          background:#fff;
          padding:10px 14px;
          border-radius:999px;
          width:min(720px, 100%);
          flex: 2 1 360px;
          min-width:180px;
        }
        .tp-search input{
          border:none; outline:none; width:100%;
          font-size:14px; background:transparent; color:#334155;
        }

        .tp-actions{
          display:flex;
          align-items:center;
          gap:10px;
          white-space:nowrap;
          flex: 1 1 auto;
          justify-content:flex-end;
        }

        .tp-btn{
          display:inline-flex;
          align-items:center;
          gap:8px;
          border:1px solid #e3e9f0;
          background:#fff;
          color:#0f172a;
          padding:10px 14px;
          border-radius:999px;
          cursor:pointer;
          font-weight:800;
        }
        .tp-btn:hover{ background:#f8fafc; }

        .tp-cart{ position:relative; }
        .tp-badge{
          position:absolute; top:-6px; left:10px;
          background:#ff5a75; color:#fff;
          font-size:11px; font-weight:800;
          padding:2px 6px; border-radius:999px;
        }
        .tp-logout{ color:#0f948d; }

        .tp-main{
          width:100%;
          max-width:1440px;
          margin:0 auto;
          padding:var(--tp-pad);
          display:grid;
          grid-template-columns: clamp(280px, 26vw, 360px) 1fr;
          gap:var(--tp-gap);
          align-items:start;
          box-sizing:border-box;
        }
        @media (max-width: 900px){
          .tp-main{ grid-template-columns: 1fr; }
          .tp-title{ display:none; }
          .tp-actions{ width:100%; justify-content:space-between; }
          .tp-search{ flex: 1 1 100%; }
        }

        /* =========================
           FILTER: SCROLL KHUSUS LIST
           ========================= */
        .tp-filter{
          background:#fff;
          border:1px solid #eef1f4;
          border-radius:14px;
          padding:14px;

          position:sticky;
          top:12px;

          /* INI KUNCI: supaya subkategori bisa discroll */
          display:flex;
          flex-direction:column;
          max-height: calc(100dvh - 24px);
          overflow: hidden;
          scrollbar-gutter: stable;
        }
        @media (max-width:900px){
          .tp-filter{ position:static; max-height: none; }
        }

        .tp-filter-group{
          flex: 1;
          min-height: 0;
          overflow-y: auto;         /* SCROLLER ADA DI SINI */
          overscroll-behavior: contain;
          padding-right: 6px;
        }

        .tp-filter h3{
          margin:10px 0 12px;
          font-size:16px;
          font-weight:900;
        }

        .tp-radio{
          display:flex;
          align-items:center;
          gap:10px;
          padding:12px 12px;
          border-radius:10px;
          cursor:pointer;
          font-size:14.5px;
          font-weight:700;
        }
        .tp-radio.active{
          background:#e8fff0;
          color:#036b0c;
        }

        .tp-cat-block{ margin-top:6px; }
        .tp-cat-head{ display:flex; align-items:center; gap:8px; }
        .tp-radio-main{ flex:1; }
        .tp-expand{
          border:1px solid #e3e9f0;
          background:#fff;
          width:40px; height:40px;
          border-radius:12px;
          cursor:pointer;
          display:flex; align-items:center; justify-content:center;
        }

        .tp-sublist{
          margin-left: 10px;
          border-left: 2px solid #eef2f7;
          padding-left: 12px;
          margin-top: 6px;
          margin-bottom: 10px;
        }
        .tp-subitem{
          padding:10px 10px;
          border-radius:10px;
          cursor:pointer;
          font-size:14px;
          color:#0f172a;
        }
        .tp-subitem:hover{ background:#f8fafc; }
        .tp-subitem.active{
          background:#e8fff0;
          color:#036b0c;
          font-weight:900;
        }

        .tp-clear{
          width:100%;
          border:none;
          background:#ef4444;
          color:#fff;
          border-radius:10px;
          padding:12px;
          font-weight:900;
          cursor:pointer;
          margin-top: 12px;
        }

        .tp-grid{
          display:grid;
          grid-template-columns: repeat(auto-fill, minmax(${GRID_MIN_CARD_PX}px, 1fr));
          gap:var(--tp-gap);
          align-content:start;
        }

        .tp-empty{
          background:#fff;
          border:1px solid #eef1f4;
          border-radius:12px;
          text-align:center;
          padding:20px;
        }

        .tp-pager{
          display:flex;
          align-items:center;
          justify-content:center;
          gap:8px;
          margin: 0 0 16px 0;
          flex-wrap:wrap;
        }
        .tp-pager.sticky{
          position: sticky;
          top: 12px;
          z-index: 5;
          background: rgba(255,255,255,.92);
          backdrop-filter: saturate(160%) blur(6px);
          border: 1px solid #e3e9f0;
          border-radius: 12px;
          padding: 8px 12px;
        }
        .tp-pageinfo{ color:#475569; margin-right:6px; font-weight:700; }
        .tp-pagebtn{
          border:1px solid #e3e9f0;
          background:#fff;
          border-radius:10px;
          padding:8px 12px;
          cursor:pointer;
          font-weight:900;
        }
        .tp-pagebtn:disabled{ opacity:.5; cursor:not-allowed; }
        .tp-pagenum{
          min-width:40px;
          padding:8px 0;
          text-align:center;
          border:1px solid #e3e9f0;
          background:#fff;
          border-radius:10px;
          cursor:pointer;
          font-weight:900;
        }
        .tp-pagenum.active{ background:#0ea5e9; border-color:#0284c7; color:#fff; }
        .tp-ellipsis{ padding:0 6px; color:#94a3b8; font-weight:900; }
      `}</style>

      {/* TOPBAR */}
      <div className="tp-topbar">
        <div className="tp-topbar-inner">
          <div className="tp-brand-wrap">
            <img src={logoKemenkes} alt="Kemenkes BKK Pangkalpinang" className="tp-logo" />
            <h1 className="tp-title">Sistem Informasi Pencatatan Data Stok</h1>
          </div>

          <div className="tp-search" role="search">
            <FaSearch style={{ color: "#74869a" }} />
            <input
              type="text"
              placeholder="Cari produk, kategori, atau subkategori…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              aria-label="Pencarian"
              autoComplete="off"
              inputMode="search"
            />
          </div>

          <div className="tp-actions">
            {isMobile && (
              <button type="button" className="tp-btn" onClick={() => setFilterOpen(true)} aria-label="Buka Filter">
                <FaFilter /> Filter
              </button>
            )}

            <button type="button" className="tp-btn tp-cart" onClick={() => setCartOpen(true)} aria-label="Buka Keranjang">
              <FaShoppingCart />
              {totalCartItems > 0 && <span className="tp-badge">{totalCartItems}</span>}
              Keranjang
            </button>

            <button
              type="button"
              className="tp-btn tp-logout"
              onClick={handleLogout}
              disabled={loggingOut}
              aria-label="Logout"
            >
              <FaSignOutAlt /> {loggingOut ? "Logout..." : "Logout"}
            </button>
          </div>
        </div>
      </div>

      {/* MAIN */}
      <div className="tp-main">
        {!isMobile && <aside>{renderFilterContent()}</aside>}

        <div style={{ display: "flex", flexDirection: "column", minHeight: 0 }} ref={gridMeasureRef}>
          <Pagination sticky />

          {pageItems.length === 0 ? (
            <div className="tp-empty">
              <p>Tidak ada produk yang cocok dengan pencarian Anda.</p>
            </div>
          ) : (
            <div className="tp-grid">
              {pageItems.map((p) => {
                const reserved = reservedById.get(p.id) || 0;
                const realStock = Number(p.stock) || 0;
                const visibleStock = Math.max(0, realStock - reserved);

                const cartItem = cart.find((x) => x.id === p.id);
                const selected = !!cartItem;
                const qty = cartItem?.qty || 0;

                return (
                  <ProductCard
                    key={p.id}
                    image={p.image || DEFAULT_IMG}
                    name={p.name}
                    category={p.main_category}
                    subcategory={p.subcategory}
                    stock={visibleStock}
                    maxStock={realStock}
                    selected={selected}
                    qty={qty}
                    onAddToCart={() => handleAddToCart(p)}
                    onQtyChange={(newQty) => handleQtyChange(p.id, newQty, realStock)}
                    onRemove={() => handleRemoveFromCart(p.id)}
                  />
                );
              })}
            </div>
          )}

          <Pagination />
        </div>
      </div>

      {/* DRAWER FILTER (mobile) */}
      <Drawer
        anchor="left"
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: "min(92vw, 420px)",
            boxSizing: "border-box",
            p: 0,
            display: "flex",
            flexDirection: "column",
            height: "100dvh",
          },
        }}
      >
        <Box
          sx={{
            p: 1.5,
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flex: "0 0 auto",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 900, display: "flex", alignItems: "center", gap: 1 }}>
            <FaFilter /> Filter
          </Typography>
          <IconButton size="small" onClick={() => setFilterOpen(false)} aria-label="Tutup">
            <FaTimes />
          </IconButton>
        </Box>

        {/* INI KUNCI: area konten drawer bisa scroll */}
        <Box sx={{ flex: "1 1 auto", overflow: "auto", p: 1.5 }}>
          {renderFilterContent()}
        </Box>
      </Drawer>

      {/* DRAWER KERANJANG */}
      <Drawer
        anchor="right"
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100vw", sm: 380 },
            boxSizing: "border-box",
            p: 0,
            display: "flex",
            flexDirection: "column",
            height: "100dvh",
          },
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "#fff",
            px: 1.5,
            pt: 1.5,
            pb: 1,
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 900 }}>
              <FaShoppingCart /> Keranjang
            </Typography>
            <IconButton size="small" onClick={() => setCartOpen(false)} aria-label="Tutup">
              <FaTimes />
            </IconButton>
          </Box>

          <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
            Total item: <b>{totalCartItems}</b>
          </Typography>

          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <Button variant="outlined" color="warning" onClick={handleClearCart} disabled={cart.length === 0} fullWidth>
              Kosongkan
            </Button>
            <Button variant="contained" color="primary" onClick={openSubmitDialog} disabled={cart.length === 0} fullWidth>
              Ajukan
            </Button>
          </Box>
        </Box>

        <Box sx={{ overflow: "auto", flex: 1, p: 1.5 }}>
          {cartLoading ? (
            <Box sx={{ p: 2 }}>Memuat keranjang…</Box>
          ) : cart.length === 0 ? (
            <Box sx={{ p: 2, color: "text.secondary" }}>Keranjang kosong. Tambahkan produk dari Dashboard.</Box>
          ) : (
            cart.map((it) => {
              const q = Number(it.qty) || 0;
              const stock = Number.isFinite(Number(it.maxStock ?? it.stock)) ? Number(it.maxStock ?? it.stock) : Infinity;

              return (
                <Box
                  key={it.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "56px 1fr",
                    gap: 1,
                    alignItems: "start",
                    py: 1,
                    borderBottom: "1px solid #e2e8f0",
                    background: "#fff",
                  }}
                >
                  <Box sx={{ width: 56, height: 56, borderRadius: 1, overflow: "hidden", bgcolor: "#fff" }}>
                    <img
                      src={it.image || DEFAULT_IMG}
                      alt={it.name}
                      width={56}
                      height={56}
                      style={{ objectFit: "contain" }}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = DEFAULT_IMG;
                      }}
                      loading="lazy"
                    />
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap title={it.name} sx={{ fontWeight: 900 }}>
                      {it.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {it.category || "-"}
                      {it.subcategory ? ` · Sub: ${it.subcategory}` : ""}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
                      <Tooltip title="Kurangi">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleQtyChange(it.id, q - 1, stock)}
                            disabled={q <= 1}
                            aria-label="Kurangi jumlah"
                          >
                            <FaMinus />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <input
                        type="number"
                        min={1}
                        max={Number.isFinite(stock) ? stock : undefined}
                        value={q}
                        onChange={(e) => handleQtyChange(it.id, Number(e.target.value), stock)}
                        style={{
                          width: 54,
                          textAlign: "center",
                          padding: 6,
                          border: "1px solid #cbd5e1",
                          borderRadius: 8,
                        }}
                        aria-label="Jumlah item"
                      />

                      <Tooltip title="Tambah">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleQtyChange(it.id, q + 1, stock)}
                            disabled={Number.isFinite(stock) && q >= stock}
                            aria-label="Tambah jumlah"
                          >
                            <FaPlus />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Hapus">
                        <IconButton size="small" color="error" onClick={() => handleRemoveFromCart(it.id)} aria-label="Hapus">
                          <FaTrash />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Drawer>

      {/* DIALOG FORM PENGAJUAN */}
      <Dialog
        open={submitOpen}
        onClose={() => (cartSubmitting ? null : setSubmitOpen(false))}
        fullWidth
        maxWidth="md"
        sx={{
          zIndex: 4000,
          "& .MuiBackdrop-root": { zIndex: 3999 },
          "& .MuiPaper-root": { zIndex: 4000 },
        }}
      >
        <DialogTitle>Form Pengajuan</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Isi <b>Sisa Stok</b> manual sesuai stok yang ada di <b>Timker/Wilker</b>, serta <b>Keperluan</b> untuk setiap item.
          </Alert>

          {submitErr && (
            <Alert severity="error" sx={{ mb: 2, whiteSpace: "pre-line" }}>
              {submitErr}
            </Alert>
          )}

          {(cart || []).map((it) => (
            <Box key={it.id} sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 900 }}>{it.name}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {it.category || "-"}
                {it.subcategory ? ` · Sub: ${it.subcategory}` : ""}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Jumlah diajukan: <b>{it.qty}</b>
              </Typography>

              <Box sx={{ mt: 1 }}>
                <TextField
                  label="Sisa Stok yang Tersedia saat ini (Timker/Wilker)"
                  type="number"
                  size="small"
                  fullWidth
                  value={it.sisa_stok ?? ""}
                  onChange={(e) => setCartField(it.id, "sisa_stok", e.target.value)}
                  inputProps={{ min: 0 }}
                  required
                />
              </Box>

              <TextField
                label="Keperluan"
                multiline
                rows={3}
                fullWidth
                sx={{ mt: 2 }}
                value={it.keperluan ?? ""}
                onChange={(e) => setCartField(it.id, "keperluan", e.target.value)}
                required
              />

              <Divider sx={{ mt: 2 }} />
            </Box>
          ))}
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={() => setSubmitOpen(false)} disabled={cartSubmitting}>
            Batal
          </Button>
          <Button variant="contained" onClick={handleSubmitCart} disabled={cartSubmitting}>
            {cartSubmitting ? "Mengirim…" : "Kirim Pengajuan"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
